import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Event } from '@/store/api/eventsApi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  StopCircle,
  PlayCircle,
  Edit,
  Trash2,
  Loader2,
  CalendarDays,
  Copy,
} from 'lucide-react';

interface EventsTableProps {
  events: Event[];
  isFetching: boolean;
  searchTerm: string;
  statusFilter: string;
  onEdit: (event: Event) => void;
  onDuplicate: (event: Event) => void;
  onDelete: (event: Event) => void;
  onToggleJoins: (id: number, currentStatus: boolean) => void;
}

export function EventsTable({
  events,
  isFetching,
  searchTerm,
  statusFilter,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleJoins,
}: EventsTableProps) {
  const navigate = useNavigate();

  return (
    <div className="relative">
      {isFetching && (
        <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center rounded-lg">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {events.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Event Date</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Seats</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {events.map((event) => (
                <tr key={event.id} className="hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-4 font-medium text-foreground max-w-xs">
                    <div className="truncate" title={event.title}>
                      {event.title}
                    </div>
                    <div className="text-xs text-muted-foreground font-normal line-clamp-2 mt-0.5 whitespace-pre-line">
                      {(() => {
                        const html = event.description || '';
                        let text = html.replace(/<br\s*\/?>/gi, '\n');
                        text = text.replace(/<\/?(p|div|h[1-6]|ul|ol|table)[^>]*>/gi, '\n');
                        text = text.replace(/<li>/gi, '\n• ');
                        if (typeof window === 'undefined') {
                          text = text.replace(/<[^>]*>?/gm, '');
                        } else {
                          const doc = new DOMParser().parseFromString(text, 'text/html');
                          text = doc.body.textContent || '';
                        }
                        return text.replace(/\n\s*\n/g, '\n').trim();
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-muted-foreground hidden md:table-cell whitespace-nowrap">
                    {event.eventDate
                      ? new Date(event.eventDate).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : '—'}
                  </td>
                  <td className="px-4 py-4 hidden lg:table-cell text-sm">
                    {event.seats ? (
                      <span
                        className={`font-medium ${
                          (event._count?.joinRequests ?? 0) >= event.seats
                            ? 'text-destructive'
                            : (event._count?.joinRequests ?? 0) >= event.seats * 0.8
                              ? 'text-amber-600'
                              : 'text-green-600'
                        }`}
                      >
                        {event._count?.joinRequests ?? 0} / {event.seats}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {event._count?.joinRequests ?? 0} / ∞
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant={event.status === 'Published' ? 'default' : 'warning'}>
                        {event.status}
                      </Badge>
                      {event.eventDate && new Date(event.eventDate) < new Date() ? (
                        <Badge
                          variant="secondary"
                          className="bg-slate-100 text-slate-500 border-slate-200"
                        >
                          Concluded
                        </Badge>
                      ) : (
                        !event.acceptingJoins && (
                          <Badge variant="destructive" className="text-xs">
                            Registration Closed
                          </Badge>
                        )
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="View Joinees"
                        className="h-8 w-8 text-blue-600 hover:text-blue-700"
                        onClick={() => navigate(`/events/join-requests?eventId=${event.id}`)}
                      >
                        <Users className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title={
                          event.acceptingJoins ? 'Stop Accepting Joins' : 'Resume Accepting Joins'
                        }
                        className={`h-8 w-8 ${
                          event.acceptingJoins
                            ? 'text-amber-600 hover:text-amber-700'
                            : 'text-green-600 hover:text-green-700'
                        }`}
                        onClick={() => onToggleJoins(event.id, event.acceptingJoins)}
                      >
                        {event.acceptingJoins ? (
                          <StopCircle className="h-4 w-4" />
                        ) : (
                          <PlayCircle className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Edit Event"
                        className="h-8 w-8"
                        onClick={() => onEdit(event)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Duplicate Event"
                        className="h-8 w-8"
                        onClick={() => onDuplicate(event)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(event)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <div className="flex justify-center mb-4">
            <CalendarDays className="h-10 w-10 text-muted-foreground/30" />
          </div>
          <p className="font-medium mb-2">No events found.</p>
          <p className="text-sm">
            {searchTerm || statusFilter
              ? 'Try adjusting your search or filter.'
              : 'Create your first event to get started.'}
          </p>
        </div>
      )}
    </div>
  );
}
